import { useCallback } from 'react';
import {
  LinkRegion,
  trackCustomLink,
  useTriggerableTrackPageView,
} from '@sparknz/tracking';

import { useSegment } from 'shared/hooks';
import { useProductDetails } from '../../../../../provider';
import { ProductDetailsActionTypes } from '../../../../../reducer/productDetails.reducer';
import {
  ActionLayerStatus,
  IfpEligibilityCheckSteps,
  InfoLayerStatus,
} from '../../../../../typings';
import {
  getProductDetailsPageViewParams,
  trackLinkedProductsEvent,
} from '../../../../../utils';
import { useWearableDevice } from '../../../hooks';
import { PrimaryActionLayer, PrimaryActionLayerContent } from '../../index';
import WearableEligibilityActionLayerContent from '../../WearableEligibilityActionLayerContent';

export default function ProductOrderActionLayer() {
  const {
    actionLayerStatus,
    dispatch,
    ifpEligibility,
    infoLayer,
    selectedDevice,
  } = useProductDetails();
  const { groupName, primaryCategory } = selectedDevice;
  const { isWearableEligibilityCheckRequired } = useWearableDevice();
  const { isBusiness } = useSegment();
  const { trackPageView } = useTriggerableTrackPageView();

  const showEligiblePlanSelector =
    ifpEligibility.activeStep === IfpEligibilityCheckSteps.LineSelection;
  const showIneligiblePlanResult =
    ifpEligibility.activeStep === IfpEligibilityCheckSteps.IneligiblePlan;

  const getActionLayerTitle = () => {
    if (isWearableEligibilityCheckRequired || showIneligiblePlanResult) {
      return 'Check eligibility';
    }
    if (showEligiblePlanSelector) {
      return 'Mobile phone plans';
    }
    return 'Choose a mobile plan';
  };

  const actionLayerTitle = getActionLayerTitle();

  const updateInfoLayer = (
    status = InfoLayerStatus.Close,
    contentKey?: string,
    isNested?: boolean,
  ) => {
    dispatch({
      type: ProductDetailsActionTypes.SetInfoLayer,
      infoLayer: {
        status,
        contentKey,
        isNested,
      },
    });
  };

  const trackCloseActionLayer = useCallback(() => {
    trackCustomLink('Close');
    trackPageView(
      getProductDetailsPageViewParams({
        groupName,
        primaryCategory,
      }),
    );
    trackLinkedProductsEvent({
      eventInfo: {
        eventName: 'Product View',
        eventAction: 'shop.prodViewAction',
      },
      device: selectedDevice,
      isBusiness,
      isChangePlan: ifpEligibility?.isChangePlan,
    });
  }, [groupName, ifpEligibility, isBusiness, primaryCategory, selectedDevice]);

  const closeActionLayer = () => {
    dispatch({
      type: ProductDetailsActionTypes.SetActionLayer,
      actionLayerStatus: ActionLayerStatus.Close,
      ifpEligibility: {
        activeStep: null,
        selectedAccountNumber: null,
        selectedLineNumber: null,
        isChangePlanOfferDetailsLoading: false,
        isChangePlan: false,
      },
      infoLayer: {
        status: InfoLayerStatus.Close,
      },
      wearableEligibility: {
        activeStep: null,
        selectedAccountNumber: null,
        selectedLineNumber: null,
      },
      changePlanOffers: null,
    });

    if (infoLayer.status === InfoLayerStatus.ContentFragment) {
      updateInfoLayer();
    }
    trackCloseActionLayer();
  };

  return (
    <LinkRegion regionId="productDetails.actionLayer">
      <PrimaryActionLayer
        isOpen={actionLayerStatus !== ActionLayerStatus.Close}
        title={actionLayerTitle}
        onClose={closeActionLayer}
      >
        {isWearableEligibilityCheckRequired ? (
          <WearableEligibilityActionLayerContent />
        ) : (
          <PrimaryActionLayerContent />
        )}
      </PrimaryActionLayer>
    </LinkRegion>
  );
}
